import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { BranchCreateRequest } from '@/api/handlers/resources/branches/requests';
import type { BranchWriteResponse } from '@/api/handlers/resources/branches/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { BranchQueryKeys } from './branchQueryKeys';

/**
 * Mutation behind the Add Branch submit. The bos-api takes the cropper payload
 * server-side (multipart) so the round-trip is a single POST. The list is
 * server-paginated (and server-ordered main-first/by-name), so where the new
 * branch lands is the server's call — we invalidate the cached pages and let
 * them refetch rather than guessing a position with an optimistic insert.
 */
export const useCreateBranchMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<BranchWriteResponse>,
        Error,
        BranchCreateRequest
    >({
        mutationFn: (body) => apiClient.branches.createBranch(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: BranchQueryKeys.details(),
                });
            }
        },
    });
};
